// @ts-nocheck
import type { ColumnDef } from '@tanstack/react-table';
import CopyButton from '@/features/api-webhook/components/CopyButton';

const CALL_STATUS_TONE: Record<string, string> = {
  connected:
    'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300',
  failed:
    'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300',
  abandoned:
    'bg-amber-100 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300',
};

const CopyableId = ({ value }: { value?: string | null }) =>
  value ? (
    <span className="inline-flex items-center gap-1">
      <span
        className="max-w-[140px] truncate font-mono text-xs text-foreground"
        title={value}
      >
        {value}
      </span>
      <CopyButton text={value} className="h-6 w-6 shrink-0 p-0" />
    </span>
  ) : (
    '-'
  );

const idColumn = (key: string, header: string): ColumnDef<any> => ({
  id: key,
  accessorFn: (r: any) => r[key] || '',
  header,
  cell: ({ row }: any) => <CopyableId value={row.original[key]} />,
});

// Campaign deductions store every DID the campaign dialled from as one
// comma-separated string ("a, b +3 more"); normalise spacing for display.
const didsOf = (r: any) =>
  String(r?.did || '')
    .split(',')
    .map((d) => d.trim())
    .filter(Boolean)
    .join(', ');

const callIdOf = (r: any) => (/^campaign-\d+$/.test(r?.call_sid || '') ? '' : r?.call_sid || '');

export const callColumns: ColumnDef<any>[] = [
  {
    id: 'call_sid',
    accessorFn: callIdOf,
    header: 'Call ID',
    cell: ({ row }: any) => <CopyableId value={callIdOf(row.original)} />,
  },
  idColumn('sip_call_id', 'SIP Call ID'),
  idColumn('trace_id', 'Trace ID'),
  {
    id: 'call_status',
    accessorFn: (r: any) => r.call_status || '',
    header: 'Call Status',
    cell: ({ row }: any) => {
      const status = row.original.call_status;
      if (!status) return '-';
      return (
        <span
          className={
            'inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize ring-1 ring-inset ' +
            (CALL_STATUS_TONE[status] || 'bg-muted text-muted-foreground ring-border')
          }
        >
          {status}
        </span>
      );
    },
  },
  {
    id: 'campaign',
    accessorFn: (r: any) => r.campaign_name || (r.campaign_id != null ? String(r.campaign_id) : ''),
    header: 'Campaign Name',
    cell: ({ row }: any) => {
      const { campaign_name, campaign_id } = row.original;
      if (campaign_name) return <span title={`ID ${campaign_id}`}>{campaign_name}</span>;
      return <span className="font-mono text-xs">{campaign_id ?? '-'}</span>;
    },
  },
  {
    id: 'did',
    accessorFn: didsOf,
    header: 'DID(s)',
    cell: ({ row }: any) => {
      const dids = didsOf(row.original);
      if (!dids) return '-';
      return (
        <span
          className="block max-w-[260px] whitespace-normal break-words font-mono text-xs"
          title={dids}
        >
          {dids}
        </span>
      );
    },
  },
];
